import React from "react";
import { cn } from "@/utils/shadcn";
import { LAB, NUM, TONE, MsTone } from "./tokens";
import { Chevron, Lab, Padlock, Tag, TagTone } from "./atoms";

/* ── DHead · section head over a 1px INK rule ───────────────────────── */

export const DHead = ({
  label,
  right,
  onClick,
  className,
}: {
  label: React.ReactNode;
  /** meta text, or the action label when onClick is given */
  right?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-baseline justify-between gap-3 border-b border-ms-ink pb-[7px]",
      className
    )}
  >
    <Lab className="text-ms-mute">{label}</Lab>
    {right != null &&
      (onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={cn(
            LAB,
            "flex-none text-[9.5px] text-ms-link transition-colors hover:text-ms-ink"
          )}
        >
          {right} →
        </button>
      ) : (
        <span className={cn(LAB, "flex-none text-[9.5px] text-ms-dim")}>{right}</span>
      ))}
  </div>
);

/* ── Fig · the figure row. Everything in the document set is one. ───── */

export type FigProps = {
  /** coin mark, brand mark or icon on the left */
  lead?: React.ReactNode;
  label: React.ReactNode;
  /** mono uppercase sub-line under the label */
  sub?: React.ReactNode;
  tag?: React.ReactNode;
  value?: React.ReactNode;
  tone?: MsTone;
  /** bold both sides + 2px ink rule underneath */
  total?: boolean;
  /** bold both sides */
  strong?: boolean;
  /** replaces the value with •••••• and a padlock */
  masked?: boolean;
  onClick?: () => void;
  className?: string;
};

export const Fig = ({
  lead,
  label,
  sub,
  tag,
  value,
  tone,
  total,
  strong,
  masked,
  onClick,
  className,
}: FigProps) => {
  const Comp: any = onClick ? "button" : "div";
  return (
    <Comp
      {...(onClick ? { type: "button", onClick } : {})}
      className={cn(
        "flex w-full items-center gap-3 border-t border-ms-line py-[11px] text-left first:border-t-0",
        total && "border-b-2 border-b-ms-ink",
        onClick && "-mx-2 min-h-[44px] px-2 transition-colors hover:bg-ms-card2",
        className
      )}
    >
      {lead ? <span className="flex-none">{lead}</span> : null}
      <span
        className={cn(
          "min-w-0 flex-1 text-[13.5px] tracking-[-0.01em]",
          (total || strong) && "font-bold"
        )}
      >
        {label}
        {sub ? (
          <span className={cn(LAB, "mt-[3px] block text-[9px] font-normal text-ms-dim")}>
            {sub}
          </span>
        ) : null}
      </span>
      {tag ? <span className="flex-none">{tag}</span> : null}
      {masked ? (
        <span
          className={cn(
            NUM,
            "inline-flex flex-none items-center gap-[7px] text-[13.5px] font-medium tracking-[0.12em] text-ms-dim"
          )}
        >
          ••••••
          <Padlock />
        </span>
      ) : value != null ? (
        <span
          className={cn(
            NUM,
            "flex-none whitespace-nowrap text-[13.5px] font-medium",
            tone && TONE[tone],
            (total || strong) && "font-bold"
          )}
        >
          {value}
        </span>
      ) : null}
      {onClick ? <Chevron className="flex-none -rotate-90 text-ms-dim" /> : null}
    </Comp>
  );
};

/* ── StatBand · bordered grid under a 3px brand rule ────────────────── */

export const StatBand = ({
  items,
  className,
}: {
  items: Array<{ label: React.ReactNode; value: React.ReactNode; sub?: React.ReactNode; tone?: MsTone }>;
  className?: string;
}) => (
  <div
    className={cn(
      "grid grid-cols-2 border border-t-[3px] border-ms-line border-t-ms-rule ms700:grid-cols-4",
      "[&>div]:border-r [&>div]:border-ms-line [&>div]:px-[15px] [&>div]:py-[14px]",
      "[&>div:nth-child(2n)]:border-r-0 [&>div:nth-child(n+3)]:border-t [&>div:nth-child(n+3)]:border-ms-line",
      "ms700:[&>div:nth-child(2n)]:border-r ms700:[&>div:last-child]:border-r-0 ms700:[&>div:nth-child(n+3)]:border-t-0",
      className
    )}
  >
    {items.map((it, i) => (
      <div key={i}>
        <Lab>{it.label}</Lab>
        <div
          className={cn(
            "mt-[5px] text-[21px] font-bold tracking-[-0.025em] tabular-nums",
            it.tone && TONE[it.tone]
          )}
        >
          {it.value}
        </div>
        {it.sub ? <div className="mt-[3px] text-[10.5px] text-ms-dim">{it.sub}</div> : null}
      </div>
    ))}
  </div>
);

/* ── Hero · Lab + the one huge figure + a standing line ─────────────── */

export const Hero = ({
  label,
  value,
  tone = "ink",
  stand,
  meta,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: MsTone;
  /** the "· defensible & ready to file" line with a dot */
  stand?: React.ReactNode;
  /** free slot under the figure (tags, deltas) */
  meta?: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("flex flex-col", className)}>
    <Lab className="block">{label}</Lab>
    <div
      className={cn(
        "mt-2.5 font-semibold leading-none tracking-[-0.035em] tabular-nums",
        TONE[tone]
      )}
      style={{ fontSize: "var(--hero)" }}
    >
      {value}
    </div>
    {stand ? (
      <div className="mt-3.5 flex items-center gap-2.5 text-[14.5px]">
        <span className="h-[7px] w-[7px] flex-none rounded-full bg-ms-green" />
        <span>{stand}</span>
      </div>
    ) : null}
    {meta ? <div className="mt-3 flex flex-wrap items-center gap-2.5">{meta}</div> : null}
  </div>
);

/* ── Quota · the tier meter ─────────────────────────────────────────── */

export const Quota = ({
  name,
  tag,
  tagTone = "warn",
  count,
  cap,
  unit = "transactions",
  note,
  /** 0–1; defaults to count/cap */
  fill,
  /** 0–1 position of the warning notch */
  notch = 0.8,
  className,
}: {
  name: React.ReactNode;
  tag?: React.ReactNode;
  tagTone?: TagTone;
  count: number | string;
  cap: number | string;
  unit?: string;
  note?: React.ReactNode;
  fill?: number;
  notch?: number;
  className?: string;
}) => {
  const pct =
    fill != null
      ? fill
      : Number(cap) > 0
      ? Math.min(1, Number(count) / Number(cap))
      : 0;
  return (
    <div className={cn("flex flex-col gap-[9px]", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[14px] font-semibold">{name}</span>
        {tag ? <Tag tone={tagTone}>{tag}</Tag> : null}
      </div>
      <div>
        <span className={cn(NUM, "text-[21px] font-semibold tracking-[-0.02em]")}>{count}</span>{" "}
        <Lab className="text-ms-dim">
          of {cap} {unit}
        </Lab>
      </div>
      <div className="relative h-[5px] overflow-hidden border border-ms-line bg-ms-card2">
        <i className="block h-full bg-ms-tan" style={{ width: `${Math.round(pct * 100)}%` }} />
        <em
          className="absolute bottom-0 top-0 w-px bg-ms-dim"
          style={{ left: `${Math.round(notch * 100)}%` }}
        />
      </div>
      {note ? <div className="text-[11.5px] leading-[1.45] text-ms-mute">{note}</div> : null}
    </div>
  );
};

/* ── DocList · the documents in a pack ──────────────────────────────── */

export const DocList = ({
  items,
  className,
}: {
  items: Array<{
    name: React.ReactNode;
    meta?: React.ReactNode;
    tag?: React.ReactNode;
    tagTone?: TagTone;
    onClick?: () => void;
  }>;
  className?: string;
}) => (
  <div className={cn("flex flex-col", className)}>
    {items.map((it, i) => {
      const Comp: any = it.onClick ? "button" : "div";
      return (
        <Comp
          key={i}
          {...(it.onClick ? { type: "button", onClick: it.onClick } : {})}
          className={cn(
            "flex w-full items-center gap-[13px] border-t border-ms-line py-[13px] text-left first:border-t-0",
            it.onClick && "-mx-2 min-h-[44px] px-2 transition-colors hover:bg-ms-card2"
          )}
        >
          <span className="min-w-0 flex-1 text-[14.5px] font-medium">
            {it.name}
            {it.meta ? (
              <Lab size={9} className="mt-[3px] block font-normal">
                {it.meta}
              </Lab>
            ) : null}
          </span>
          {it.tag ? <Tag tone={it.tagTone || "neu"}>{it.tag}</Tag> : null}
        </Comp>
      );
    })}
  </div>
);

/* ── OfferList · the upsell grid, 3-up from 820 ─────────────────────── */

export const OfferList = ({
  items,
  className,
}: {
  items: Array<{
    name: React.ReactNode;
    price: React.ReactNode;
    body?: React.ReactNode;
    cta?: React.ReactNode;
    onClick?: () => void;
  }>;
  className?: string;
}) => (
  <div className={cn("grid grid-cols-1 gap-0 min-[820px]:grid-cols-3 min-[820px]:gap-6", className)}>
    {items.map((it, i) => (
      <div
        key={i}
        className="border-t border-ms-line py-4 min-[820px]:border-t-2 min-[820px]:border-t-ms-ink min-[820px]:pt-3.5"
      >
        <div className="flex items-baseline justify-between gap-2.5">
          <span className="text-[15px] font-semibold tracking-[-0.015em]">{it.name}</span>
          <span className="text-[15px] font-semibold tabular-nums">{it.price}</span>
        </div>
        {it.body ? (
          <p className="mt-[7px] text-[12.5px] leading-[1.5] text-ms-mute">{it.body}</p>
        ) : null}
        {it.cta ? (
          <button
            type="button"
            onClick={it.onClick}
            className={cn(
              LAB,
              "mt-1 inline-flex min-h-[40px] items-center text-[9.5px] text-ms-link transition-colors hover:text-ms-green"
            )}
          >
            {it.cta} →
          </button>
        ) : null}
      </div>
    ))}
  </div>
);

/* ── Steps · numbered rows with mint circle numerals ────────────────── */

export const Steps = ({
  items,
  className,
}: {
  items: Array<{ title: React.ReactNode; body?: React.ReactNode }>;
  className?: string;
}) => (
  <div className={cn("flex flex-col", className)}>
    {items.map((it, i) => (
      <div key={i} className="flex gap-3 border-t border-ms-line py-[13px] first:border-t-0">
        <span
          className={cn(
            NUM,
            "grid h-[22px] w-[22px] flex-none place-items-center rounded-full bg-ms-mint-soft text-[10px] font-semibold text-ms-link"
          )}
        >
          {i + 1}
        </span>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold tracking-[-0.015em]">{it.title}</div>
          {it.body ? (
            <div className="mt-[3px] text-[12px] leading-[1.4] text-ms-mute">{it.body}</div>
          ) : null}
        </div>
      </div>
    ))}
  </div>
);
